"use client"

import { useState, useEffect } from "react"
import { Tag, ChevronDown, ChevronUp } from "lucide-react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

interface TagCloudProps {
    tags: { name: string; count: number }[]
    selectedTag?: string
    onTagClick: (tag: string) => void
    className?: string
}

export default function TagCloud({ tags, selectedTag, onTagClick, className }: TagCloudProps) {
    const [showAll, setShowAll] = useState(false)
    const displayTags = showAll ? tags : tags.slice(0, 15)

    if (tags.length === 0) {
        return null
    }

    return (
        <div className={cn("bg-card border rounded-lg p-4", className)}>
            <div className="flex items-center gap-2 mb-3">
                <Tag className="w-4 h-4 text-primary" />
                <h3 className="font-semibold text-sm">Popular Tags</h3>
            </div>

            <div className="flex flex-wrap gap-2 mb-3">
                {displayTags.map((tag) => (
                    <button
                        key={tag.name}
                        onClick={() => onTagClick(tag.name)}
                        className={cn(
                            "inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border transition-colors",
                            selectedTag === tag.name
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background hover:bg-accent border-border"
                        )}
                    >
                        <span>{tag.name}</span>
                        <span className="text-xs opacity-70">({tag.count})</span>
                    </button>
                ))}
            </div>

            {tags.length > 15 && (
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAll(!showAll)}
                    className="w-full text-xs h-8"
                >
                    {showAll ? (
                        <>
                            <ChevronUp className="w-3 h-3 mr-1" />
                            Show Less
                        </>
                    ) : (
                        <>
                            <ChevronDown className="w-3 h-3 mr-1" />
                            Show All ({tags.length} tags)
                        </>
                    )}
                </Button>
            )}
        </div>
    )
}